'use client'

import * as React from 'react'
import * as ScrollAreaPrimitive from '@radix-ui/react-scroll-area'

import { cn } from '@/lib/utils'

// A VR controller ray has no wheel and Radix's own scrollbar thumb is a fiddly target to
// grab at arm's length, so a plain wheel-scroll ScrollArea is otherwise unreachable in a
// headset. dragToScroll adds touch-style "grab anywhere and drag" scrolling on top of it —
// opt-in via a prop so it doesn't change scrolling anywhere else in the app.
function useDragToScroll(enabled: boolean) {
  const viewportRef = React.useRef<HTMLDivElement>(null)
  React.useEffect(() => {
    if (!enabled) return
    const viewport = viewportRef.current
    if (!viewport) return

    let dragging = false
    let startY = 0
    let startScrollTop = 0
    let pointerId: number | null = null

    // [data-slot="slider"] (the Radix Slider root, not just its thumb) matters: clicking the
    // *track* to jump the value is a normal, common slider interaction, and it doesn't carry
    // role="slider" (only the thumb does) — excluding only the thumb let every track click
    // get captured as a scroll-drag instead, breaking sliders throughout the panel.
    const isInteractive = (target: EventTarget | null) =>
      target instanceof Element &&
      target.closest('button, a, input, select, textarea, [role="switch"], [data-slot^="slider"]') != null

    const onPointerDown = (e: PointerEvent) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return
      if (isInteractive(e.target)) return
      dragging = true
      pointerId = e.pointerId
      startY = e.clientY
      startScrollTop = viewport.scrollTop
      viewport.setPointerCapture(e.pointerId)
    }
    const onPointerMove = (e: PointerEvent) => {
      if (!dragging || e.pointerId !== pointerId) return
      viewport.scrollTop = startScrollTop - (e.clientY - startY)
    }
    const endDrag = (e: PointerEvent) => {
      if (e.pointerId !== pointerId) return
      dragging = false
      pointerId = null
      try {
        viewport.releasePointerCapture(e.pointerId)
      } catch {}
    }

    viewport.addEventListener('pointerdown', onPointerDown)
    viewport.addEventListener('pointermove', onPointerMove)
    viewport.addEventListener('pointerup', endDrag)
    viewport.addEventListener('pointercancel', endDrag)
    return () => {
      viewport.removeEventListener('pointerdown', onPointerDown)
      viewport.removeEventListener('pointermove', onPointerMove)
      viewport.removeEventListener('pointerup', endDrag)
      viewport.removeEventListener('pointercancel', endDrag)
    }
  }, [enabled])
  return viewportRef
}

// Radix's Viewport already sets `overflow: scroll` internally, so a plain mouse wheel
// scrolls it with no JS involved. wheelAssist is for the cases that don't go through a
// real native wheel event at all — a gamepad/analog-stick-driven cursor emulating input via
// synthetic events, some remote-desktop or accessibility tooling, etc. — where relying on
// the browser's own wheel-to-scroll plumbing isn't reliable. Opt-in, like dragToScroll,
// so it doesn't change scroll feel anywhere it isn't asked for.
function useWheelAssist(enabled: boolean, viewportRef: React.RefObject<HTMLDivElement | null>) {
  React.useEffect(() => {
    if (!enabled) return
    const viewport = viewportRef.current
    if (!viewport) return
    const onWheel = (e: WheelEvent) => {
      viewport.scrollTop += e.deltaY
      viewport.scrollLeft += e.deltaX
      e.preventDefault()
    }
    viewport.addEventListener('wheel', onWheel, { passive: false })
    return () => viewport.removeEventListener('wheel', onWheel)
  }, [enabled, viewportRef])
}

function ScrollArea({
  className,
  children,
  dragToScroll = false,
  wheelAssist = false,
  ...props
}: React.ComponentProps<typeof ScrollAreaPrimitive.Root> & { dragToScroll?: boolean; wheelAssist?: boolean }) {
  const viewportRef = useDragToScroll(dragToScroll)
  useWheelAssist(wheelAssist, viewportRef)
  return (
    <ScrollAreaPrimitive.Root
      data-slot="scroll-area"
      className={cn('relative', className)}
      {...props}
    >
      <ScrollAreaPrimitive.Viewport
        ref={viewportRef}
        data-slot="scroll-area-viewport"
        className={cn(
          'focus-visible:ring-ring/50 size-full rounded-[inherit] transition-[color,box-shadow] outline-none focus-visible:ring-[3px] focus-visible:outline-1',
          dragToScroll && 'cursor-grab touch-none active:cursor-grabbing',
        )}
      >
        {children}
      </ScrollAreaPrimitive.Viewport>
      <ScrollBar />
      <ScrollAreaPrimitive.Corner />
    </ScrollAreaPrimitive.Root>
  )
}

function ScrollBar({
  className,
  orientation = 'vertical',
  ...props
}: React.ComponentProps<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>) {
  return (
    <ScrollAreaPrimitive.ScrollAreaScrollbar
      data-slot="scroll-area-scrollbar"
      orientation={orientation}
      className={cn(
        'flex touch-none p-px transition-colors select-none',
        orientation === 'vertical' &&
          'h-full w-2.5 border-l border-l-transparent',
        orientation === 'horizontal' &&
          'h-2.5 flex-col border-t border-t-transparent',
        className,
      )}
      {...props}
    >
      <ScrollAreaPrimitive.ScrollAreaThumb
        data-slot="scroll-area-thumb"
        className="bg-border relative flex-1 rounded-full"
      />
    </ScrollAreaPrimitive.ScrollAreaScrollbar>
  )
}

export { ScrollArea, ScrollBar }
